import { useState, type FormEvent } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { useSupabaseQuery } from '../../hooks/useSupabaseQuery';
import { PageHeader } from '../../components/ui/PageHeader';
import { EmptyState } from '../../components/ui/EmptyState';
import { isAdmin } from '../../lib/roles';
import type { GastoConCategoria } from '../../types/domain';
import type { CategoriaGastoRow, FormaPagoRow } from '../../types/database.types';

/**
 * Módulo de Gastos (DAF v2, sección 20). Escritura restringida a admin —
 * misma regla que impone la política RLS `gastos_admin` de schema.sql.
 * Un gasto nunca se borra: se anula (estado='anulado'), siguiendo la
 * regla de negocio 30 del DAF ("los registros financieros no deberían
 * eliminarse físicamente").
 *
 * Todo gasto se registra vía el RPC `registrar_gasto`
 * (supabase/schema_gastos_caja.sql), que además genera automáticamente
 * el egreso correspondiente en movimientos_caja SI hay una caja abierta
 * — así un gasto nunca queda desconectado de la caja del día.
 */
export function GastosList() {
  const { perfil } = useAuth();
  const [mostrarForm, setMostrarForm] = useState(false);
  const puedeEscribir = isAdmin(perfil?.rol);

  const { data: categorias } = useSupabaseQuery<CategoriaGastoRow[]>(async () =>
    supabase.from('categorias_gastos').select('*').order('nombre'),
  );

  const { data: formasPago } = useSupabaseQuery<FormaPagoRow[]>(async () =>
    supabase.from('formas_pago').select('*').eq('estado', 'activo').order('nombre'),
  );

  const {
    data: gastos,
    loading,
    error,
    refetch,
  } = useSupabaseQuery<GastoConCategoria[]>(async () => {
    const { data, error } = await supabase
      .from('gastos')
      .select('*, categorias_gastos ( nombre )')
      .order('fecha', { ascending: false })
      .limit(100);

    if (error) return { data: null, error };

    const items = (data as unknown as Array<Record<string, unknown>>).map((row) => ({
      ...(row as unknown as GastoConCategoria),
      categoria_nombre: (row.categorias_gastos as { nombre: string } | null)?.nombre ?? '—',
    }));

    return { data: items, error: null };
  });

  async function anular(id: string) {
    await supabase.from('gastos').update({ estado: 'anulado' }).eq('id', id);
    refetch();
  }

  function alGuardar() {
    setMostrarForm(false);
    refetch();
  }

  return (
    <div>
      <PageHeader
        title="Gastos"
        description="Gastos administrativos y operativos — DAF v2, sección 20"
        actions={
          puedeEscribir ? (
            <button className="btn-primary" onClick={() => setMostrarForm((v) => !v)}>
              {mostrarForm ? 'Cancelar' : 'Registrar gasto'}
            </button>
          ) : undefined
        }
      />

      {mostrarForm && perfil && (
        <RegistrarGastoForm
          salonId={perfil.salon_id}
          categorias={categorias ?? []}
          formasPago={formasPago ?? []}
          onGuardado={alGuardar}
          onCancelar={() => setMostrarForm(false)}
        />
      )}

      {loading && <p className="text-sm text-onyx/50">Cargando…</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}

      {gastos && gastos.length === 0 && <EmptyState title="Sin gastos registrados todavía" />}

      {gastos && gastos.length > 0 && (
        <div className="card overflow-x-auto p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-onyx/10 text-left text-onyx/50">
                <th className="px-4 py-3 font-medium">Fecha</th>
                <th className="px-4 py-3 font-medium">Categoría</th>
                <th className="px-4 py-3 font-medium">Descripción</th>
                <th className="px-4 py-3 font-medium">Beneficiario</th>
                <th className="px-4 py-3 font-medium">Valor</th>
                <th className="px-4 py-3 font-medium">Estado</th>
                {puedeEscribir && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {gastos.map((g) => (
                <tr key={g.id} className="border-b border-onyx/5 last:border-0">
                  <td className="px-4 py-3 text-onyx/60">
                    {new Date(g.fecha).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })}
                  </td>
                  <td className="px-4 py-3 text-onyx">{g.categoria_nombre}</td>
                  <td className="px-4 py-3 text-onyx/70">{g.descripcion ?? '—'}</td>
                  <td className="px-4 py-3 text-onyx/70">{g.beneficiario ?? '—'}</td>
                  <td className="px-4 py-3 font-medium text-onyx">${g.valor.toLocaleString('es-CO')}</td>
                  <td className="px-4 py-3">
                    <span className={`badge ${g.estado === 'anulado' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-800'}`}>
                      {g.estado === 'anulado' ? 'Anulado' : 'Activo'}
                    </span>
                  </td>
                  {puedeEscribir && (
                    <td className="px-4 py-3 text-right">
                      {g.estado !== 'anulado' && (
                        <button className="text-xs text-red-600 hover:underline" onClick={() => anular(g.id)}>
                          Anular
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

interface RegistrarGastoFormProps {
  salonId: string;
  categorias: CategoriaGastoRow[];
  formasPago: FormaPagoRow[];
  onGuardado: () => void;
  onCancelar: () => void;
}

function RegistrarGastoForm({ salonId, categorias, formasPago, onGuardado, onCancelar }: RegistrarGastoFormProps) {
  const [categoriaId, setCategoriaId] = useState('');
  const [valor, setValor] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [formaPagoId, setFormaPagoId] = useState('');
  const [beneficiario, setBeneficiario] = useState('');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const valorNum = Number(valor);
    if (!categoriaId || !valorNum || valorNum <= 0) {
      setError('Selecciona una categoría y un valor mayor que cero');
      return;
    }

    setGuardando(true);
    const { error: err } = await supabase.rpc('registrar_gasto', {
      p_salon_id: salonId,
      p_categoria_id: categoriaId,
      p_valor: valorNum,
      p_descripcion: descripcion || null,
      p_forma_pago_id: formaPagoId || null,
      p_beneficiario: beneficiario || null,
      p_soporte_url: null,
    });
    setGuardando(false);

    if (err) {
      setError(err.message);
      return;
    }

    onGuardado();
  }

  return (
    <form onSubmit={handleSubmit} className="card mb-4 space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-onyx/80">Categoría *</span>
          <select required className="input-field" value={categoriaId} onChange={(e) => setCategoriaId(e.target.value)}>
            <option value="" disabled>
              Selecciona una categoría
            </option>
            {categorias.map((c) => (
              <option key={c.id} value={c.id}>
                {c.nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-onyx/80">Valor *</span>
          <input
            required
            type="number"
            min={1}
            step="0.01"
            className="input-field"
            value={valor}
            onChange={(e) => setValor(e.target.value)}
          />
        </label>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-onyx/80">Forma de pago</span>
          <select className="input-field" value={formaPagoId} onChange={(e) => setFormaPagoId(e.target.value)}>
            <option value="">Sin especificar</option>
            {formasPago.map((f) => (
              <option key={f.id} value={f.id}>
                {f.nombre}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="mb-1 block text-sm font-medium text-onyx/80">Beneficiario</span>
          <input className="input-field" value={beneficiario} onChange={(e) => setBeneficiario(e.target.value)} />
        </label>
      </div>

      <label className="block">
        <span className="mb-1 block text-sm font-medium text-onyx/80">Descripción</span>
        <textarea className="input-field" rows={2} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} />
      </label>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex gap-3">
        <button type="submit" disabled={guardando} className="btn-primary">
          {guardando ? 'Guardando…' : 'Registrar gasto'}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancelar}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
